<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <FranchiseSelect
        v-model="formData.so_additional_service_price_franchise_id"
        :label="t('Franquia')"
        data-testid="so-additional-service-prices-franchise-select"
        :disabled="isEditMode"
        required
      />
      <small v-if="errors.so_additional_service_price_franchise_id" class="error-message">{{ errors.so_additional_service_price_franchise_id }}</small>
    </div>

    <div class="form-field">
      <SelectList
        elem-id="soAdditionalServicePriceAdditionalId"
        data-testid="so-additional-service-prices-additional-select"
        v-model="formData.so_additional_service_price_additional_id"
        :itens-select="additionalOptions"
        :label="t('Serviço adicional')"
        :placeholder="t('Selecione o serviço adicional')"
        :auto-select-first="false"
      />
      <small v-if="errors.so_additional_service_price_additional_id" class="error-message">{{ errors.so_additional_service_price_additional_id }}</small>
    </div>

    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-field">
          <input-currency
            elem-id="soAdditionalServicePricePrice"
            data-testid="so-additional-service-prices-price-input"
            v-model="formData.so_additional_service_price_price"
            :label="t('Preço da franquia')"
            :placeholder="t('Digite o preço')"
          />
          <small v-if="errors.so_additional_service_price_price" class="error-message">{{ errors.so_additional_service_price_price }}</small>
        </div>
      </div>

      <div class="flex-item-half">
        <div class="form-field">
          <input-number
            elem-id="soAdditionalServicePriceDeadlineDays"
            data-testid="so-additional-service-prices-deadline-days-input"
            v-model="formData.so_additional_service_price_deadline_days"
            :label="t('Prazo adicional (dias)')"
            :placeholder="t('Dias somados ao prazo do item')"
            :min="0"
            :maxFractionDigits="0"
            fluid
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import InputNumber from '../../inputs/InputNumber.vue';
import SelectList from '../../inputs/SelectList.vue';
import { useSOAdditionalServicePrices } from '../../composables/useSOAdditionalServicePrices';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import { emitValidationFailed } from '../../composables/useFormValidation';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface Props {
  entryId?: Id | null;
  isEditMode?: boolean;
}

interface SelectOption {
  ID: Id;
  Name: string;
}

interface KVItem {
  value: Id;
  label: string;
}

const props = withDefaults(defineProps<Props>(), {
  entryId: null,
  isEditMode: false
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

const { t } = useI18n();

const { getById, save } = useSOAdditionalServicePrices();
const { fetchKV: fetchAdditionalsKV } = useSOCatalog('additional_services');

const entryIdNumber = computed<number | null>(() => {
  if (!props.entryId) return null;
  return typeof props.entryId === 'string' ? parseInt(props.entryId, 10) : props.entryId;
});

const formData = reactive<{
  so_additional_service_price_franchise_id: Id | null;
  so_additional_service_price_additional_id: Id | undefined;
  so_additional_service_price_price: number | undefined;
  so_additional_service_price_deadline_days: number | undefined;
}>({
  so_additional_service_price_franchise_id: null,
  so_additional_service_price_additional_id: undefined,
  so_additional_service_price_price: undefined,
  so_additional_service_price_deadline_days: undefined
});

const errors = reactive<{
  so_additional_service_price_franchise_id: string;
  so_additional_service_price_additional_id: string;
  so_additional_service_price_price: string;
}>({
  so_additional_service_price_franchise_id: '',
  so_additional_service_price_additional_id: '',
  so_additional_service_price_price: ''
});

const isLoading = ref<boolean>(false);
const additionalOptions = ref<SelectOption[]>([]);

const loadOptions = async (): Promise<void> => {
  try {
    const additionalsKV = await fetchAdditionalsKV() as KVItem[];
    additionalOptions.value = additionalsKV.map((item: KVItem) => ({
      ID: item.value,
      Name: item.label
    }));
  } catch (error: unknown) {
    logError('SOAdditionalServicePricesForm', 'loadOptions', 'Error loading options', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar opcoes') + ': ' + message);
  }
};

const loadForEdit = async (): Promise<void> => {
  if (!entryIdNumber.value) {
    logWarn('SOAdditionalServicePricesForm', 'loadForEdit', 'No entryId provided');
    return;
  }

  isLoading.value = true;

  try {
    const entry = await getById(entryIdNumber.value);
    if (!entry) {
      throw new Error('Dados do preço não encontrados');
    }

    // O wire de `get` já fala em REAIS — o form edita o que o wire fala.
    formData.so_additional_service_price_franchise_id = entry.so_additional_service_price_franchise_id ?? null;
    formData.so_additional_service_price_additional_id = entry.so_additional_service_price_additional_id ?? undefined;
    formData.so_additional_service_price_price = entry.so_additional_service_price_price ?? undefined;
    formData.so_additional_service_price_deadline_days = entry.so_additional_service_price_deadline_days ?? undefined;

    clearErrors();
  } catch (error: unknown) {
    logError('SOAdditionalServicePricesForm', 'loadForEdit', 'Error loading entry', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar o preço do adicional') + ': ' + message);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const clearErrors = (): void => {
  errors.so_additional_service_price_franchise_id = '';
  errors.so_additional_service_price_additional_id = '';
  errors.so_additional_service_price_price = '';
};

const validateForm = (): boolean => {
  clearErrors();
  let isValid = true;

  if (!formData.so_additional_service_price_franchise_id) {
    errors.so_additional_service_price_franchise_id = t('Campo obrigatório');
    isValid = false;
  }

  if (!formData.so_additional_service_price_additional_id) {
    errors.so_additional_service_price_additional_id = t('Campo obrigatório');
    isValid = false;
  }

  if (formData.so_additional_service_price_price == null || formData.so_additional_service_price_price < 0) {
    errors.so_additional_service_price_price = t('Campo obrigatório');
    isValid = false;
  }

  if (!isValid) {
    emitValidationFailed('SOAdditionalServicePricesForm', errors);
  }
  return isValid;
};

const resetForm = (): void => {
  formData.so_additional_service_price_franchise_id = null;
  formData.so_additional_service_price_additional_id = undefined;
  formData.so_additional_service_price_price = undefined;
  formData.so_additional_service_price_deadline_days = undefined;
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  if (!validateForm()) {
    logWarn('SOAdditionalServicePricesForm', 'handleSubmit', 'Validation failed');
    return;
  }

  isLoading.value = true;

  try {
    const payload = {
      so_additional_service_price_additional_id: Number(formData.so_additional_service_price_additional_id),
      // REAIS na tela -> CENTAVOS no wire de salvar (o mesmo da matriz).
      so_additional_service_price_price: Math.round((formData.so_additional_service_price_price || 0) * 100),
      so_additional_service_price_franchise_id: Number(formData.so_additional_service_price_franchise_id),
      ...(formData.so_additional_service_price_deadline_days != null
        ? { so_additional_service_price_deadline_days: parseInt(String(formData.so_additional_service_price_deadline_days), 10) }
        : {}),
      ...(props.isEditMode && entryIdNumber.value
        ? { so_additional_service_price_id: entryIdNumber.value }
        : {})
    };

    await save(payload);
    eventBus.emit(
      'globalMessage.success',
      props.isEditMode ? t('Preço do adicional atualizado') : t('Preço do adicional criado')
    );

    emit('submit-success');
    resetForm();
  } catch (error: unknown) {
    logError('SOAdditionalServicePricesForm', 'handleSubmit', 'Error submitting form', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar o preço do adicional') + ': ' + message);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.entryId, async (newId) => {
  if (newId && props.isEditMode) {
    await nextTick();
    await loadForEdit();
  }
});

onMounted(async () => {
  await loadOptions();

  if (props.isEditMode && entryIdNumber.value) {
    await loadForEdit();
  }
});

defineExpose({
  loadForEdit,
  handleSubmit,
  resetForm,
  validateForm
});
</script>

<style scoped>
.flex-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.flex-item-half {
  flex: 1;
  min-width: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
